import { Trans, t } from '@lingui/macro';
import { MoreVertical, MessageCircle, UserX, Crown, History } from "lucide-react";
import { useState, useEffect } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { Room } from "../../models/Room";
import { User } from "../../models/User";
import { UserBlock } from "../../models/UserBlock";
import { FEATURE_USER_BLOCKING } from "../../config/features";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import ProfileImg from "./ProfileImg";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { UserAvatar } from "./UserAvatar";
import { Badge } from "../../components/ui/badge";
import { useMyPermissions } from '../../context/WorkspacePermissionContext';

interface UserPopOverProps {
  user: User;
  content: React.ReactNode;
  room?: Room;
  isCreator?: boolean;
  isGuest?: boolean;
  canKick?: boolean;
  onKick?: (user: User) => void;
}

export const UserPopOver: React.FC<UserPopOverProps> = ({
  user,
  content,
  room,
  isCreator = false,
  isGuest = false,
  canKick = false,
  onKick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showKickDialog, setShowKickDialog] = useState(false);
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [grantPending, setGrantPending] = useState(false);
  const [hasHistoryAccess, setHasHistoryAccess] = useState(false);
  const history = useHistory();
  const { toast } = useToast();
  const perms = useMyPermissions();

  const isMe = My.user?.id === user.id;
  // 현재 유저가 guest이면 1:1 채팅 시작 불가
  const isMeGuest = perms?.canSearchMembers === false;
  // 방장 여부
  const isRoomCreator = room ? Room.isCreator(room, My.user.id) : false;

  // canReadPastMessages 상태 초기화
  useEffect(() => {
    if (room) {
      const permitted = (room as any).canReadPastMessages as string[] | undefined;
      setHasHistoryAccess(permitted?.includes(user.id) ?? false);
    }
  }, [room, user.id]);

  async function handleGrantPastAccess() {
    if (!room) return;
    setGrantPending(true);
    setShowGrantDialog(false);
    try {
      const result = await Fire.grantPastMessageAccess(room.id, user.id);
      setHasHistoryAccess(true);
      toast({
        title: t`Past message access granted (${result.backfilledCount} messages)`,
        duration: 2000,
      });
    } catch {
      toast({ title: t`Failed to grant access`, duration: 2000 });
    } finally {
      setGrantPending(false);
      setIsOpen(false);
    }
  }

  async function gotoOneToOne() {
    setIsOpen(false);
    let existingRoom = Array.from(My.Rooms.values()).find(r => r.isOneToOne && r.userIds.includes(user.id) && r.userIds.includes(My.user.id));
    if (!existingRoom) {
      existingRoom = new Room(`${user.name}${Util.OneToOneDelimiter}${My.user.name}`, My.user.id);
      existingRoom.isOneToOne = true;
      existingRoom.workspaceId = room?.workspaceId || null;
      existingRoom.users = [My.user, user];
      existingRoom.userIds = [My.user.id, user.id];
      await Fire.saveRoom(existingRoom);
    }

    setTimeout(() => {
      history.replace(`/channel/${existingRoom!.id}`);
    }, 100);
  }

  async function handleBlock() {
    if (My.UserBlocks.get(user.id) == null) {
      let block = new UserBlock(user, My.user.id);
      My.UserBlocks.set(block.blockedUser.id, block);
      await Fire.saveUserBlock(block);
      toast({
        title: t`${user.name} has been blocked. Reload to apply.`,
        duration: 3000,
      });
    }
    setShowBlockDialog(false);
    setIsOpen(false);
  }

  function handleKick() {
    onKick?.(user);
    setShowKickDialog(false);
    setIsOpen(false);
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen} modal>
        <PopoverTrigger asChild>
          <span className="cursor-pointer">
            {content}
          </span>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0 rounded-xl shadow-xl border-border" align="start">
          {/* 유저 정보 헤더 - Centered Style */}
          <div className="flex flex-col items-center p-6 border-b border-border bg-muted/30">
            <UserAvatar src={user?.photoUrl} name={user.name} className="w-20 h-20 mb-3" />

            <div className="flex items-center gap-1.5 mb-1">
              <span className="font-bold text-lg text-[color:rgb(var(--text-title))]">{user.name}</span>
              {isMe && <Badge variant="secondary" className="text-meta-sm h-5">You</Badge>}
              {isCreator && (
                <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/10 text-meta-sm h-5 gap-0.5 border-primary/20">
                  <Crown className="w-3 h-3" />
                  <Trans>Owner</Trans>
                </Badge>
              )}
              {isGuest && (
                <Badge variant="secondary" className="bg-muted text-muted-foreground hover:bg-muted text-meta-sm h-5 border-border">
                  <Trans>Guest</Trans>
                </Badge>
              )}
            </div>

            <a href={`mailto:${user.email}`} className="text-sm text-muted-foreground hover:text-primary hover:underline transition-colors mb-4">
              {user.email}
            </a>

            {user.description && (
              <div className="w-full mt-2 text-sm text-[color:rgb(var(--text-body))] bg-background border border-border px-3 py-2.5 rounded-lg text-center italic">
                "{user.description}"
              </div>
            )}
          </div>

          {/* 액션 버튼들 - 본인 프로필이 아닐 때만 렌더링 */}
          {!isMe && (
            <div className="p-2 flex flex-col gap-1 bg-background">
              {!isMeGuest && (
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-10 px-4 rounded-lg hover:bg-primary/5 hover:text-primary font-medium transition-colors"
                  onClick={gotoOneToOne}
                  data-cy="one2one"
                >
                  <MessageCircle className="w-4 h-4" />
                  <Trans>Open One-to-one</Trans>
                </Button>
              )}

              {FEATURE_USER_BLOCKING && (
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-10 px-4 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  onClick={() => setShowBlockDialog(true)}
                >
                  <UserX className="w-4 h-4" />
                  <Trans>Block {user.name}</Trans>
                </Button>
              )}

              {canKick && !isCreator && (
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-10 px-4 rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors"
                  onClick={() => setShowKickDialog(true)}
                >
                  <UserX className="w-4 h-4" />
                  <Trans>Kick out</Trans>
                </Button>
              )}

              {/* 이전 대화 열람 권한 부여 (방장 전용, 본인/방장 제외) */}
              {isRoomCreator && !isCreator && (
                hasHistoryAccess ? (
                  <div className="flex items-center gap-3 h-10 px-4 text-sm text-primary/70">
                    <History className="w-4 h-4" />
                    <Trans>Past messages accessible</Trans>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-10 px-4 rounded-lg hover:bg-primary/5 hover:text-primary font-medium transition-colors"
                    onClick={() => setShowGrantDialog(true)}
                    disabled={grantPending}
                  >
                    <History className="w-4 h-4" />
                    <Trans>Grant past message access</Trans>
                  </Button>
                )
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* 차단 확인 다이얼로그 */}
      {FEATURE_USER_BLOCKING && (
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Block ${user.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Messages created by this person will be hidden for you. We do not inform the person that you have blocked them.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={handleBlock} className="bg-destructive hover:bg-destructive/90">
              {t`Block`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}

      {/* 강퇴 확인 다이얼로그 */}
      <AlertDialog open={showKickDialog} onOpenChange={setShowKickDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Kick out ${user.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`This member will be removed from the channel.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={handleKick} className="bg-destructive hover:bg-destructive/90">
              {t`Kick out`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 이전 대화 열람 권한 부여 확인 다이얼로그 */}
      <AlertDialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Grant past message access?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                {user.name} will be able to view and search past messages in this channel.
                This action cannot be undone.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={handleGrantPastAccess}>
              {t`Grant access`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default UserPopOver;
